/**
 * theme.js — shared utilities. Small on purpose. Components import from here.
 * Everything is progressive: pages work without this file.
 */

export const routes = () => window.Shopify?.routes ?? { root: '/' };

/** Announce to screen readers via the layout's live region. */
export function announce(message) {
  const region = document.getElementById('LiveRegion');
  if (!region) return;
  region.textContent = '';
  requestAnimationFrame(() => { region.textContent = message; });
}

/** Fetch config for Shopify AJAX endpoints. */
export function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/** Section Rendering API: fetch rendered HTML for section ids on a URL. Returns {id: DocumentFragment}. */
export async function renderSections(sectionIds, url = window.location.pathname) {
  const u = new URL(url, window.location.origin);
  u.searchParams.set('sections', sectionIds.join(','));
  const res = await fetch(u.toString(), { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`Section render failed: ${res.status}`);
  const json = await res.json();
  const parser = new DOMParser();
  const out = {};
  for (const id of sectionIds) {
    if (!json[id]) continue;
    out[id] = parser.parseFromString(json[id], 'text/html');
  }
  return out;
}

/** Replace the inner HTML of a selector from a freshly rendered section document. */
export function swapInnerHTML(targetSelector, doc, sourceSelector = targetSelector) {
  const target = document.querySelector(targetSelector);
  const source = doc.querySelector(sourceSelector);
  if (target && source) target.innerHTML = source.innerHTML;
  return target;
}

/** Focus trap + Escape-to-close for <dialog> and drawers. Returns a release function. */
export function trapFocus(container, { onEscape } = {}) {
  const selectors = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const focusables = () => Array.from(container.querySelectorAll(selectors)).filter((el) => el.offsetParent !== null || el === document.activeElement);
  const previouslyFocused = document.activeElement;
  const first = focusables()[0];
  first?.focus();
  function onKey(e) {
    if (e.key === 'Escape' && onEscape) { e.preventDefault(); onEscape(); return; }
    if (e.key !== 'Tab') return;
    const list = focusables();
    if (!list.length) return;
    const firstEl = list[0], lastEl = list[list.length - 1];
    if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); }
    else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); }
  }
  container.addEventListener('keydown', onKey);
  return () => { container.removeEventListener('keydown', onKey); previouslyFocused?.focus?.(); };
}

/** Debounce. */
export function debounce(fn, wait = 200) {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
}

/** Money formatting using the shop's format string (set on window.themeMoneyFormat by the layout). */
export function formatMoney(cents, format = window.themeMoneyFormat || '{{amount}}') {
  const value = (cents / 100).toFixed(2);
  const [whole, dec] = value.split('.');
  const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const map = {
    '{{amount}}': `${withCommas}.${dec}`,
    '{{amount_no_decimals}}': withCommas,
    '{{amount_with_comma_separator}}': `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${dec}`,
    '{{amount_no_decimals_with_comma_separator}}': whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.'),
    '{{amount_with_space_separator}}': `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')},${dec}`,
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (m) => map[m.replace(/\s/g, '')] ?? map['{{amount}}']);
}

/** Custom events the theme uses. Components dispatch/listen on document. */
export const EVENTS = {
  cartUpdate: 'theme:cart:update',      // detail: { cart, source }
  cartOpen: 'theme:cart:open',
  cartAdded: 'theme:cart:added',        // detail: { trigger } - only when settings.cart_add_feedback is 'fly'
  variantChange: 'theme:variant:change', // detail: { variant, product }
};

export function emit(name, detail = {}) {
  document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
}

/**
 * After a successful add: either open the cart drawer (default) or, when the store opts into the
 * fly-to-cart feedback and the header cart icon is on screen, keep the shopper where they are.
 */
export function afterAdd(trigger) {
  const icon = document.querySelector('.header__cart');
  const rect = icon?.getBoundingClientRect();
  const iconVisible = rect && rect.width > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
  if (document.body.dataset.cartAdd === 'fly' && iconVisible && customElements.get('cart-fly')) {
    emit(EVENTS.cartAdded, { trigger });
  } else {
    emit(EVENTS.cartOpen, { trigger });
  }
}

/** Define a custom element once. */
export function define(name, ctor) {
  if (!customElements.get(name)) customElements.define(name, ctor);
}

// Money format for JS from the layout (set by a tiny inline script there).
